/**
 * When taking stock has stopped being a wait.
 *
 * The step dispatches the population, then watches the gate criterion until it
 * reports a registered count. That is right while the job is running and wrong
 * the moment it is not: the watch had no other exit, so a job that failed left
 * the page spinning "Taking stock…" forever, with a note promising it "changes
 * on its own".
 *
 * Observed on 2026-08-22: three dispatches across two runs, every one refused
 * by the database within about thirty-five seconds
 * (`record dispositions may only be superseded explicitly`), and the run's own
 * event stream recorded none of it - REGISTERED, AXIS_PENDING, STRUCTURING,
 * SUPERSEDED and nothing else. The API says nothing about the failure, so the
 * page cannot report the cause. What it can report is the measurement: it was
 * dispatched, and nothing has been registered since.
 *
 * That distinction is the whole of this module. It says a job has produced
 * nothing for longer than a working one would; it does not say why, because
 * nothing available here knows why.
 */

/**
 * How long a dispatched population may register nothing before the page stops
 * calling it progress.
 *
 * A population that is running writes its first manifest line in seconds - it
 * is one row per file and the first row does not wait for the last. The
 * refusals measured here came back in about thirty-five seconds. Ninety is
 * comfortably past both and still short enough that nobody sits watching a
 * spinner for the length of a meeting.
 */
export const STALL_MS = 90_000;

export interface WatchFacts {
  /** When this page dispatched it, in epoch milliseconds. */
  startedAt: number | null;
  now: number;
  /** Manifest lines registered so far, from the gate's own criterion. */
  registered: number;
}

export type PopulationVerdict =
  | { kind: 'idle' }
  /** Dispatched and nothing registered yet, still within the window. */
  | { kind: 'waiting'; elapsedMs: number }
  /** Registering. Any count above zero proves the job is alive. */
  | { kind: 'working'; registered: number }
  /** Dispatched, nothing registered, past the window. Not a wait any more. */
  | { kind: 'stalled'; elapsedMs: number };

export function populationVerdict(facts: WatchFacts): PopulationVerdict {
  if (facts.startedAt === null) return { kind: 'idle' };
  // Any line at all means the job is alive, however slow. A large scope may
  // take minutes to finish and that is not this module's business.
  if (facts.registered > 0) return { kind: 'working', registered: facts.registered };
  const elapsedMs = Math.max(0, facts.now - facts.startedAt);
  return elapsedMs > STALL_MS ? { kind: 'stalled', elapsedMs } : { kind: 'waiting', elapsedMs };
}

/** Whether the page should keep polling. A stall ends the watch. */
export function keepWatching(verdict: PopulationVerdict): boolean {
  return verdict.kind === 'waiting' || verdict.kind === 'working';
}

/**
 * What to say about a stall, without claiming a cause.
 *
 * Deliberately not a diagnosis. The failure this was built for is a database
 * refusal the API does not expose, so the page knows only that it asked and
 * nothing came back. Naming the run is the useful part: it is what someone
 * reading the executor's log needs to find it.
 */
export function stalledSentence(elapsedMs: number, runId: string): string {
  const minutes = Math.max(1, Math.round(elapsedMs / 60_000));
  return (
    `Taking stock was started ${minutes} minute${minutes === 1 ? '' : 's'} ago and no manifest line has been ` +
    `registered. A population that is running registers its first within seconds, so this is not a slow job. ` +
    `Nothing on this page can clear it, and this run cannot be named until a manifest exists. ` +
    `The failure is server-side and does not reach this screen; run ${runId} is what identifies it in the log.`
  );
}
